import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Th, Td, Pagination } from "./AdminUsers";
import { CheckCircle2, XCircle, ArrowUpRight, FileQuestion, X } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["", "certificate", "passport", "employment", "recommendation", "identity"];
const STATUSES = ["", "Submitted", "Awaiting Evidence", "Escalated", "Resolved (Approved)", "Resolved (Rejected)", "Closed"];

export default function AdminDisputes() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [targetType, setTargetType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/disputes", { params: { target_type: targetType, status_filter: statusFilter, skip, limit } });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {}
  }, [targetType, statusFilter, skip]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    let note = "";
    if (decision === "request_evidence") {
      note = prompt("What evidence should we ask for?") || "";
      if (!note) return;
    } else if (decision === "approve" || decision === "reject") {
      note = prompt("Decision note (optional):") || "";
    }
    try {
      await api.post(`/admin/disputes/${id}/decision`, { decision, note });
      toast.success("Decision recorded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout subtitle="Module 7" title="Dispute Management" actions={<div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} disputes</div>}>
      <div className="flex flex-wrap gap-3 mb-5" data-testid="disputes-filters">
        <select value={targetType} onChange={(e) => setTargetType(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="disputes-type-filter">
          {TYPES.map((t) => <option key={t} value={t} className="bg-ec-ink">{t || "All types"}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="disputes-status-filter">
          {STATUSES.map((s) => <option key={s} value={s} className="bg-ec-ink">{s || "All statuses"}</option>)}
        </select>
      </div>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="disputes-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <Th>Filed</Th>
                <Th>Filer</Th>
                <Th>Target</Th>
                <Th>Reason</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`dispute-row-${d.id}`}>
                  <Td className="text-xs font-mono-plex text-zinc-400">{(d.created_at || "").slice(0, 10)}</Td>
                  <Td>
                    <div className="text-xs font-mono-plex">{d.user_ethic_id || d.user_id}</div>
                  </Td>
                  <Td>
                    <Badge className="bg-white/10 border-white/10 rounded-sm">{d.target_type}</Badge>
                    <div className="text-[10px] font-mono-plex text-zinc-500 mt-1">{d.target_id?.slice(0, 8)}…</div>
                  </Td>
                  <Td className="max-w-[300px] text-xs text-zinc-300">{d.reason}</Td>
                  <Td><DisputeStatus status={d.status} /></Td>
                  <Td className="text-right">
                    {(d.status === "Submitted" || d.status === "Awaiting Evidence" || d.status === "Escalated") && (
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        <Button size="sm" className="rounded-sm h-7 bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-1" onClick={() => decide(d.id, "approve")} data-testid={`dispute-approve-${d.id}`}><CheckCircle2 className="w-3 h-3" />Approve</Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 border-ec-error/40 text-ec-error gap-1 hover:bg-ec-error/10" onClick={() => decide(d.id, "reject")} data-testid={`dispute-reject-${d.id}`}><XCircle className="w-3 h-3" />Reject</Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 border-white/10 text-white gap-1 hover:bg-white/10" onClick={() => decide(d.id, "request_evidence")} data-testid={`dispute-request-evidence-${d.id}`}><FileQuestion className="w-3 h-3" />Request</Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 border-amber-500/40 text-amber-400 gap-1 hover:bg-amber-500/10" onClick={() => decide(d.id, "escalate")} data-testid={`dispute-escalate-${d.id}`}><ArrowUpRight className="w-3 h-3" />Escalate</Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 border-white/10 text-zinc-400 gap-1 hover:bg-white/10" onClick={() => decide(d.id, "close")} data-testid={`dispute-close-${d.id}`}><X className="w-3 h-3" />Close</Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-zinc-500" data-testid="disputes-empty">No disputes.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />
    </AdminLayout>
  );
}

function DisputeStatus({ status }) {
  const cls = (status || "").includes("Approved") ? "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30"
    : (status || "").includes("Rejected") ? "bg-ec-error/20 text-ec-error border-ec-error/40"
    : status === "Escalated" ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
    : status === "Awaiting Evidence" ? "bg-violet-500/10 text-violet-300 border-violet-500/30"
    : status === "Closed" ? "bg-white/5 text-zinc-500 border-white/10"
    : "bg-white/10 text-white border-white/10";
  return <Badge className={`rounded-sm border ${cls}`}>{status}</Badge>;
}
